import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { formatCurrency } from '../utils/currencyFormatter';
import api, { accountsAPI } from '../services/api';
import SearchableDropdown from '../components/SearchableDropdown';
import { 
  Globe, 
  Send, 
  Shield, 
  ArrowLeft,
  Building,
  CreditCard,
  AlertTriangle,
  CheckCircle,
  Info,
  Flag
} from 'lucide-react';

const WireTransfer = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const notification = useNotification();
  
  const [transferType, setTransferType] = useState('domestic');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [recipientType, setRecipientType] = useState('new');
  const [amount, setAmount] = useState('');
  const [purpose, setPurpose] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  
  // Form data for different transfer types
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    bankName: '',
    routingNumber: '',
    accountNumber: '',
    swiftCode: '',
    iban: '',
    country: '',
    bankAddress: ''
  });
  
  // Currency formatter for user's currency
  const formatCurrencyWithUserCurrency = (amount) => {
    return formatCurrency(amount, user?.currency_code || 'USD');
  };
  
  // Fetch user accounts on component mount
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        setAccountsLoading(true);
        const accountsRes = await accountsAPI.getAccounts();
        
        if (accountsRes.status === 'success') {
          setAccounts(accountsRes.data.accounts);
          // Set first account as default if none selected
          if (!selectedAccount && accountsRes.data.accounts.length > 0) {
            setSelectedAccount(accountsRes.data.accounts[0].id.toString());
          }
        } else {
          console.log('Accounts API returned error:', accountsRes);
          setAccounts([]);
        }
      } catch (error) {
        console.error('Error fetching accounts:', error);
        notification.showError('Failed to load accounts. Please try refreshing the page.');
        setAccounts([]);
      } finally {
        setAccountsLoading(false);
      }
    };

    fetchAccounts();
  }, [selectedAccount, notification]);

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Form validation
  const validateForm = () => {
    if (!selectedAccount) {
      notification.showError('Please select an account');
      return false;
    }
    if (!amount || parseFloat(amount) <= 0) {
      notification.showError('Please enter a valid amount');
      return false;
    }
    if (!formData.firstName || !formData.lastName) {
      notification.showError('Please enter recipient name');
      return false;
    }
    if (!formData.bankName) {
      notification.showError('Please enter bank name');
      return false;
    }
    
    // Type-specific validation
    if (transferType === 'domestic') {
      if (!formData.routingNumber || formData.routingNumber.length !== 9) {
        notification.showError('Please enter a valid 9-digit routing number');
        return false;
      }
      if (!formData.accountNumber) {
        notification.showError('Please enter account number');
        return false;
      }
    } else if (transferType === 'international') {
      if (!formData.swiftCode) {
        notification.showError('Please enter SWIFT/BIC code');
        return false;
      }
      if (!formData.accountNumber) {
        notification.showError('Please enter account number');
        return false;
      }
      if (!formData.country) {
        notification.showError('Please select country');
        return false;
      }
    } else if (transferType === 'sepa') {
      if (!formData.iban) {
        notification.showError('Please enter IBAN');
        return false;
      }
    }
    
    return true;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    
    try {
      const transferData = {
        fromAccount: selectedAccount,
        amount: parseFloat(amount),
        memo: message || `Wire transfer to ${formData.firstName} ${formData.lastName}`,
        recipientName: `${formData.firstName} ${formData.lastName}`,
        bankName: formData.bankName,
        ...(transferType === 'domestic' && {
          routingNumber: formData.routingNumber,
          accountNumber: formData.accountNumber
        }),
        ...(transferType === 'international' && {
          swiftCode: formData.swiftCode,
          accountNumber: formData.accountNumber,
          country: formData.country
        }),
        ...(transferType === 'sepa' && {
          iban: formData.iban,
          swiftCode: formData.swiftCode,
          country: 'EU'
        })
      };

      let response;
      if (transferType === 'domestic') {
        response = await api.post('/wire-transfers/domestic', transferData);
      } else if (transferType === 'international') {
        response = await api.post('/wire-transfers/international', transferData);
      } else if (transferType === 'sepa') {
        response = await api.post('/wire-transfers/sepa', transferData);
      }

      if (response.data?.status === 'success') {
        notification.showSuccess('Wire transfer initiated successfully!');
        navigate('/dashboard');
      } else {
        notification.showError(response.data?.message || 'Transfer failed');
      }
    } catch (error) {
      console.error('Transfer error:', error);
      notification.showError(error.response?.data?.message || 'Transfer failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Transfer types with dynamic fees based on user currency
  const transferTypes = [
    {
      id: 'domestic',
      title: 'Domestic Wire',
      description: 'Send money within the United States',
      fee: 25.00,
      feeDisplay: formatCurrencyWithUserCurrency(25.00),
      time: 'Same day',
      icon: Building,
      color: 'from-blue-500 to-blue-600'
    },
    {
      id: 'international',
      title: 'International Wire',
      description: 'Send money anywhere in the world',
      fee: 45.00,
      feeDisplay: formatCurrencyWithUserCurrency(45.00),
      time: '1-3 business days',
      icon: Globe,
      color: 'from-purple-500 to-purple-600'
    },
    {
      id: 'sepa',
      title: 'SEPA Transfer',
      description: 'Send money within Europe (EUR only)',
      fee: 15.00,
      feeDisplay: formatCurrencyWithUserCurrency(15.00),
      time: 'Next business day',
      icon: Flag,
      color: 'from-green-500 to-green-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-4">
              <Link to="/dashboard" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <ArrowLeft className="w-6 h-6 text-gray-600" />
              </Link>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-red-500 to-red-600 rounded-xl flex items-center justify-center">
                  <Globe className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Wire Transfers</h1>
                  <p className="text-sm text-gray-500">Secure global money transfers</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Transfer Type Selection */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Choose Transfer Type</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {transferTypes.map((type) => (
              <div
                key={type.id}
                onClick={() => setTransferType(type.id)}
                className={`p-6 rounded-2xl border-2 cursor-pointer transition-all ${
                  transferType === type.id
                    ? 'border-red-500 bg-red-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className={`w-12 h-12 bg-gradient-to-r ${type.color} rounded-xl flex items-center justify-center mb-4`}>
                  <type.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{type.title}</h3>
                <p className="text-gray-600 mb-4">{type.description}</p>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Fee:</span>
                    <span className="font-semibold">{type.feeDisplay}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Processing:</span>
                    <span className="font-semibold">{type.time}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white rounded-2xl shadow-sm p-8"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-6">Transfer Details</h3>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* From Account */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">From Account</label>
                  {accountsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
                      <span className="ml-3 text-gray-600">Loading accounts...</span>
                    </div>
                  ) : (
                    <SearchableDropdown
                      options={accounts.map(account => ({
                        value: account.id.toString(),
                        label: `${account.account_name || account.name} • ${formatCurrencyWithUserCurrency(account.balance)}`,
                        account: account
                      }))}
                      value={selectedAccount}
                      onChange={(value) => setSelectedAccount(value)}
                      placeholder="Search and select account..."
                      searchPlaceholder="Search accounts..."
                      getSearchText={(option) => `${option.account.account_name || option.account.name} ${option.account.account_type?.name || ''}`}
                      renderOption={(option) => (
                        <div className="flex items-center space-x-3 p-2">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            option.account.account_type?.name?.toLowerCase().includes('checking') ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'
                          }`}>
                            <CreditCard className="w-5 h-5" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{option.account.account_name || option.account.name}</p>
                            <p className="text-sm text-gray-500">****{option.account.account_number?.slice(-4) || '0000'}</p>
                            <p className="text-sm font-semibold text-gray-900">
                              {formatCurrencyWithUserCurrency(option.account.balance)}
                            </p>
                          </div>
                        </div>
                      )}
                      renderSelected={(option) => (
                        <div className="flex items-center space-x-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            option.account.account_type?.name?.toLowerCase().includes('checking') ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'
                          }`}>
                            <CreditCard className="w-4 h-4" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{option.account.account_name || option.account.name}</p>
                            <p className="text-sm text-gray-600">{formatCurrencyWithUserCurrency(option.account.balance)}</p>
                          </div>
                        </div>
                      )}
                    />
                  )}
                </div>

                {/* Recipient Information */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-4">Recipient</label>
                  
                  {/* Recipient Type Selector */}
                  <div className="flex space-x-2 mb-4">
                    <button
                      type="button"
                      onClick={() => setRecipientType('new')}
                      className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                        recipientType === 'new'
                          ? 'bg-red-100 text-red-700 border border-red-300'
                          : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                      }`}
                    >
                      New Recipient
                    </button>
                    <button
                      type="button"
                      onClick={() => setRecipientType('saved')}
                      className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                        recipientType === 'saved'
                          ? 'bg-red-100 text-red-700 border border-red-300'
                          : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                      }`}
                    >
                      Saved Recipients
                    </button>
                  </div>

                  {recipientType === 'new' && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                          <input
                            type="text"
                            value={formData.firstName}
                            onChange={(e) => handleInputChange('firstName', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                            placeholder="First name"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                          <input
                            type="text"
                            value={formData.lastName}
                            onChange={(e) => handleInputChange('lastName', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                            placeholder="Last name"
                            required
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name</label>
                        <input
                          type="text"
                          value={formData.bankName}
                          onChange={(e) => handleInputChange('bankName', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                          placeholder="Recipient's bank name"
                          required
                        />
                      </div>

                      {transferType === 'domestic' && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Routing Number</label>
                            <input
                              type="text"
                              value={formData.routingNumber}
                              onChange={(e) => handleInputChange('routingNumber', e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                              placeholder="9-digit routing number"
                              maxLength="9"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Account Number</label>
                            <input
                              type="text"
                              value={formData.accountNumber}
                              onChange={(e) => handleInputChange('accountNumber', e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                              placeholder="Recipient's account number"
                              required
                            />
                          </div>
                        </>
                      )}

                      {transferType === 'international' && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">SWIFT/BIC Code</label>
                            <input
                              type="text"
                              value={formData.swiftCode}
                              onChange={(e) => handleInputChange('swiftCode', e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                              placeholder="SWIFT/BIC code"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">IBAN or Account Number</label>
                            <input
                              type="text"
                              value={formData.accountNumber}
                              onChange={(e) => handleInputChange('accountNumber', e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                              placeholder="IBAN or account number"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                            <SearchableDropdown
                              options={[
                                { value: "GB", label: "United Kingdom", flag: "🇬🇧" },
                                { value: "DE", label: "Germany", flag: "🇩🇪" },
                                { value: "FR", label: "France", flag: "🇫🇷" },
                                { value: "CA", label: "Canada", flag: "🇨🇦" },
                                { value: "AU", label: "Australia", flag: "🇦🇺" },
                                { value: "JP", label: "Japan", flag: "🇯🇵" },
                                { value: "IN", label: "India", flag: "🇮🇳" },
                                { value: "BR", label: "Brazil", flag: "🇧🇷" },
                                { value: "MX", label: "Mexico", flag: "🇲🇽" },
                                { value: "CH", label: "Switzerland", flag: "🇨🇭" },
                                { value: "SG", label: "Singapore", flag: "🇸🇬" },
                                { value: "HK", label: "Hong Kong", flag: "🇭🇰" },
                                { value: "AE", label: "United Arab Emirates", flag: "🇦🇪" },
                                { value: "ZA", label: "South Africa", flag: "🇿🇦" },
                                { value: "NG", label: "Nigeria", flag: "🇳🇬" },
                                { value: "GH", label: "Ghana", flag: "🇬🇭" },
                                { value: "KE", label: "Kenya", flag: "🇰🇪" }
                              ]}
                              value={formData.country}
                              onChange={(value) => handleInputChange('country', value)}
                              placeholder="Search and select country..."
                              searchPlaceholder="Search countries..."
                              renderOption={(option) => (
                                <div className="flex items-center space-x-3">
                                  <span className="text-xl">{option.flag}</span>
                                  <span className="font-medium">{option.label}</span>
                                </div>
                              )}
                              renderSelected={(option) => (
                                <div className="flex items-center space-x-3">
                                  <span className="text-lg">{option.flag}</span>
                                  <span className="font-medium">{option.label}</span>
                                </div>
                              )}
                            />
                          </div>
                        </>
                      )}

                      {transferType === 'sepa' && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">IBAN</label>
                            <input
                              type="text"
                              value={formData.iban}
                              onChange={(e) => handleInputChange('iban', e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                              placeholder="Enter IBAN"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">BIC/SWIFT Code</label>
                            <input
                              type="text"
                              value={formData.swiftCode}
                              onChange={(e) => handleInputChange('swiftCode', e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                              placeholder="BIC/SWIFT code"
                            />
                          </div>
                        </>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Bank Address</label>
                        <textarea
                          rows={3}
                          value={formData.bankAddress}
                          onChange={(e) => handleInputChange('bankAddress', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                          placeholder="Bank address"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Amount</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-lg font-semibold"
                    />
                                         <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                       <span className="text-gray-400 font-medium text-lg">
                         {user?.currency_code === 'USD' ? '$' : 
                          user?.currency_code === 'EUR' ? '€' : 
                          user?.currency_code === 'GBP' ? '£' : 
                          user?.currency_code === 'NGN' ? '₦' : 
                          user?.currency_code === 'GHS' ? '₵' : 
                          user?.currency_code === 'KES' ? 'KSh' : 
                          user?.currency_code === 'ZAR' ? 'R' : 
                          user?.currency_code === 'CAD' ? 'C$' : 
                          user?.currency_code === 'AUD' ? 'A$' : 
                          user?.currency_code === 'JPY' ? '¥' : 
                          user?.currency_code === 'CHF' ? 'CHF' : 
                          user?.currency_code === 'CNY' ? '¥' : 
                          user?.currency_code === 'INR' ? '₹' : 
                          user?.currency_code === 'BRL' ? 'R$' : 
                          user?.currency_code === 'MXN' ? '$' : 
                          user?.currency_code || '$'}
                       </span>
                     </div>
                  </div>
                  <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-center space-x-2 text-sm text-blue-700">
                      <Info className="w-4 h-4" />
                      <span>Transfer fee: {transferTypes.find(t => t.id === transferType)?.feeDisplay}</span>
                    </div>
                  </div>
                </div>

                {/* Purpose */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Purpose of Transfer</label>
                  <select 
                    value={purpose}
                    onChange={(e) => setPurpose(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  >
                    <option value="">Select purpose</option>
                    <option value="family_support">Family Support</option>
                    <option value="business">Business Transaction</option>
                    <option value="education">Education</option>
                    <option value="property">Property Purchase</option>
                    <option value="investment">Investment</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Message (Optional)</label>
                  <textarea
                    rows={3}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Add a message for the recipient"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center space-x-2 ${
                    isLoading 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700'
                  } text-white`}
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      <span>Review Transfer</span>
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Transfer Summary */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-2xl shadow-sm p-6"
            >
              <h3 className="text-lg font-bold text-gray-900 mb-4">Transfer Summary</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Transfer Type:</span>
                  <span className="font-semibold capitalize">{transferType.replace('_', ' ')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Transfer Amount:</span>
                  <span className="font-semibold">{amount ? formatCurrencyWithUserCurrency(parseFloat(amount)) : formatCurrencyWithUserCurrency(0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Transfer Fee:</span>
                  <span className="font-semibold">{transferTypes.find(t => t.id === transferType)?.feeDisplay}</span>
                </div>
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex justify-between">
                    <span className="text-gray-900 font-semibold">Total Amount:</span>
                    <span className="font-bold text-red-600">
                                             {amount ? formatCurrencyWithUserCurrency(parseFloat(amount) + parseFloat(transferTypes.find(t => t.id === transferType)?.fee || 0)) : formatCurrencyWithUserCurrency(0)}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Security Features */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-white rounded-2xl shadow-sm p-6"
            >
              <div className="flex items-center space-x-3 mb-4">
                <Shield className="w-6 h-6 text-green-600" />
                <h3 className="text-lg font-bold text-gray-900">Security Features</h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-sm text-gray-700">256-bit SSL encryption</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-sm text-gray-700">Multi-factor authentication</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-sm text-gray-700">Real-time fraud monitoring</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-sm text-gray-700">Secure SWIFT network</span>
                </div>
              </div>
            </motion.div>

            {/* Important Notes */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-yellow-50 rounded-2xl p-6 border border-yellow-200"
            >
              <div className="flex items-center space-x-3 mb-4">
                <AlertTriangle className="w-6 h-6 text-yellow-600" />
                <h3 className="text-lg font-bold text-yellow-900">Important Notes</h3>
              </div>
              <div className="space-y-2 text-sm text-yellow-800">
                <p>• Wire transfers cannot be cancelled once sent</p>
                <p>• International transfers may take 1-5 business days</p>
                <p>• Additional fees may apply from correspondent banks</p>
                <p>• Ensure all recipient details are accurate</p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WireTransfer; 